import {Routes, Route, useLocation} from "react-router-dom"
import {Box, Grid, Typography, Tooltip, IconButton, Link, Alert} from "@mui/material"
import SnoozeIcon from "@mui/icons-material/Snooze"
import WarningIcon from "@mui/icons-material/Warning"
import React from "react"
import {useNavigate} from "react-router"

import {Tabs} from "./Tabs"
import {CustomerMenu} from "./CustomerMenu"
import {Overview} from "./Overview"
import {Accounts} from "./Accounts"
import {PaymentPlans} from "./PaymentPlans/PaymentPlans"
import {Notes} from "./Notes"
import {NotesV2} from "./NotesV2"
import {Transactions} from "./Transactions"
import {Activities} from "./Activities"
import {CustomerOffers} from "./CustomerOffers/CustomerOffers"
import {OffersAvailableBadge} from "./CustomerOffers/OffersAvailableBadge"
import {OfferModal} from "./OfferModal"
import IconDiscount from "./IconDiscount.svg"
import {CustomerEligibleOffersModal} from "./CustomerEligibleOffersModal"

import {SystemOfRecordChip} from "@indebted/components/SystemOfRecordChip"
import {useNotification} from "@indebted/components/Notification"
import {api, Customer, SettlementOfferResponse} from "@indebted/api"
import {Search} from "@indebted/components/Search"

function CustomerCard({customer, reload}: {customer: Customer; reload: () => void}) {
	const location = useLocation()
	const search = new URLSearchParams(location.search)

	return (
		<>
			<Search value={search.get("q")} backURL={search.get("back")} />
			<Box p={3} pt={0}>
				{customer.SystemOfRecord === "Latitude" && (
					<Grid container style={{marginBottom: "16px"}}>
						<Grid size={12}>
							<Alert severity="warning">
								This is a Latitude customer. No action should be taken in Rocket.
								{customer.Latitude && (
									<>
										{" "}
										{customer.Latitude.Link && (
											<Link
												href={customer.Latitude.Link}
												target="_blank"
												rel="noopener noreferrer"
											>
												View in Latitude
											</Link>
										)}
										<br />
										Collection Comms Enabled:{" "}
										{customer.Latitude.CollectionCommsEnabled ? "Yes" : "No"}
										<br />
										Payments Enabled: {customer.Latitude.PaymentsEnabled ? "Yes" : "No"}
									</>
								)}
							</Alert>
						</Grid>
					</Grid>
				)}
				<Grid container alignItems="center">
					<Grid size="auto" style={{display: "flex", alignItems: "center"}}>
						<CustomerStatus customer={customer} />
					</Grid>
					<Grid size="grow" style={{flexGrow: 1}}>
						<Box display="flex" alignItems="center">
							<Typography variant="h5">{customer.FullName}</Typography>
							<SystemOfRecordChip systemOfRecord={customer.SystemOfRecord} />
							{customer.EnableRocketOffersTab && <OffersAvailableBadge />}
						</Box>
					</Grid>
					<CustomerActions customer={customer} />
					<Grid size="auto">
						<CustomerMenu customer={customer} reload={reload} />
					</Grid>
				</Grid>
				<AccountClosureAlert customer={customer} />
			</Box>

			<Routes>
				<Route path="/" element={<OverviewTab customer={customer} reload={reload} />} />
				<Route path="/notes" element={<NoteTab customer={customer} reload={reload} />} />
				<Route path="/accounts/*" element={<AccountsTab customer={customer} reload={reload} />} />
				<Route path="/paymentplans" element={<PaymentPlansTab customer={customer} reload={reload} />} />
				<Route path="/activities/*" element={<ActivitiesTab customer={customer} />} />
				<Route path="/transactions" element={<TransactionsTab customer={customer} />} />
				{customer.EnableRocketOffersTab && <Route path="/offers" element={<OffersTab customer={customer} />} />}
			</Routes>
		</>
	)
}

function CustomerStatus({customer}: {customer: Customer}): JSX.Element | null {
	const snoozeMap: Record<string, JSX.Element | null> = {
		true: (
			<Tooltip title={`until ${customer.SnoozedUntil}`} placement="right">
				<SnoozeIcon style={{paddingRight: "0.5rem", fontSize: "2rem"}} />
			</Tooltip>
		),
		false: null,
	}

	const escalationMap: Record<string, JSX.Element | null> = {
		true: (
			<Tooltip title={customer.Escalation} placement="right">
				<WarningIcon style={{paddingRight: "0.5rem", fontSize: "2rem"}} />
			</Tooltip>
		),
		false: null,
	}

	return escalationMap[String(!!customer.Escalation)] || snoozeMap[String(!!customer.CommunicationSnoozed)]
}

function CustomerActions({customer}: {customer: Customer}) {
	const location = useLocation()
	const navigation = useNavigate()
	const [offerData, setOfferData] = React.useState<SettlementOfferResponse | undefined>()
	const {notification} = useNotification()

	function setupDiscountOffer() {
		if (customer.EnableRocketOffersTab) {
			navigation("offers" + location.search)
			return
		}
		api.customer
			.getSettlementOffer(customer.ID)
			.then((response) => {
				setOfferData(response)
			})
			.catch((error: {Message: string}) => {
				notification.error(error.Message)
			})
	}

	if (customer.CustomerPaidCommissions) {
		return null
	}

	return (
		<>
			{offerData ? (
				<OfferModal offerData={offerData} customerID={customer.ID} onClose={() => setOfferData(undefined)} />
			) : null}
			<Grid size="auto">
				<CustomerEligibleOffersModal customerID={customer.ID} />
			</Grid>
			{customer.EnableCreateSettlementOffer && (
				<Grid size="auto">
					<IconButton onClick={setupDiscountOffer} title="Settle account with discount">
						<img src={IconDiscount} width={20} />
					</IconButton>
				</Grid>
			)}
		</>
	)
}

function AccountClosureAlert({customer}: {customer: Customer}) {
	const allAccounts = [...customer.OtherAccounts, ...customer.OutstandingAccounts, ...customer.PaymentPlanAccounts]
	const closedAccounts = allAccounts.filter((acc) => acc.Closed)
	const hasClosedAccounts = closedAccounts.length > 0
	const hasAllAccountsClosed = hasClosedAccounts && allAccounts.length == closedAccounts.length

	return (
		<>
			{hasClosedAccounts && (
				<Grid size="grow" style={{flexGrow: 1, paddingTop: "5px"}}>
					{hasAllAccountsClosed ? (
						<Alert severity="error">Account(s) closed for collection</Alert>
					) : (
						<Alert severity="warning">Customer has both open and closed accounts for collection</Alert>
					)}
				</Grid>
			)}
		</>
	)
}

function OverviewTab({customer, reload}: {customer: Customer; reload: () => void}) {
	return (
		<>
			<Tabs index={0} customer={customer} />
			<Overview customer={customer} reload={reload} />
		</>
	)
}

function NoteTab({customer, reload}: {customer: Customer; reload: () => void}) {
	return (
		<>
			<Tabs index={1} customer={customer} />
			{customer.UseNotesV2 ? (
				<NotesV2 customerID={customer.ID} reload={reload} systemOfRecord={customer.SystemOfRecord} />
			) : (
				<Notes
					customerID={customer.ID}
					notes={customer.Notes}
					reload={reload}
					systemOfRecord={customer.SystemOfRecord}
				/>
			)}
		</>
	)
}

function AccountsTab({customer, reload}: {customer: Customer; reload: () => void}) {
	return (
		<>
			<Tabs index={2} customer={customer} />
			<Accounts customer={customer} reload={reload} />
		</>
	)
}

function TransactionsTab({customer}: {customer: Customer}) {
	return (
		<>
			<Tabs index={3} customer={customer} />
			<Transactions transactions={customer.Transactions} />
		</>
	)
}

function PaymentPlansTab({customer, reload}: {customer: Customer; reload: () => void}) {
	return (
		<>
			<Tabs index={4} customer={customer} />
			<PaymentPlans
				activePaymentPlans={customer.ActivePaymentPlans}
				reload={reload}
				customerID={customer.ID}
				totalCustomerBalance={customer.TotalCustomerAvailableBalanceInCents / 100}
			/>
		</>
	)
}

function ActivitiesTab({customer}: {customer: Customer}) {
	return (
		<>
			<Tabs index={5} customer={customer} />
			<Activities customerID={customer.ID} />
		</>
	)
}

function OffersTab({customer}: {customer: Customer}) {
	return (
		<>
			<Tabs index={6} customer={customer} />
			<CustomerOffers
				enableCustomerOffersV2={customer.EnableCustomerOffersV2}
				minInstalmentAmount={10}
				balanceInCents={customer.TotalCustomerAvailableBalanceInCents}
			/>
		</>
	)
}

export {CustomerCard}
